import type { FC } from "react";
import type { ObjPathProxy } from "ts-object-path";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import {
	ClientPortfolioDomainEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailDates } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailDates";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChart } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChart";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { FundListSection } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/FundListSection";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { getPaidAllLogic } from "web_myplann/Portfolio/Helpers/getPaidAllLogic";
import {
	type InvestmentDomain,
	isInvestmentDomain,
} from "web_myplann/Portfolio/Helpers/isInvestmentDomain";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import {
	getPortfolioFrequencyValue,
	getPortfolioValueDate,
} from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

type FundDomainConfig = {
	toolsTitle: ObjPathProxy<MyPlannResourceDictionary, string>;
	usesCurrencySignForPaidAll: boolean;
	hidesEvaluation: boolean;
};

const FUND_DOMAIN_CONFIG: Record<InvestmentDomain, FundDomainConfig> = {
	[ClientPortfolioDomainEnum.Investments]: {
		toolsTitle: PageResources.Investment.InvestmentTool.title,
		usesCurrencySignForPaidAll: false,
		hidesEvaluation: false,
	},
	[ClientPortfolioDomainEnum.Commodities]: {
		toolsTitle: PageResources.ToolHistory.Commodity.title,
		usesCurrencySignForPaidAll: false,
		hidesEvaluation: true,
	},
	[ClientPortfolioDomainEnum.Cryptocurrencies]: {
		toolsTitle: PageResources.ToolHistory.Cryptocurrency.title,
		usesCurrencySignForPaidAll: true,
		hidesEvaluation: true,
	},
};

export const FundPortfolioSection: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
	onUpdateValue,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailLoader hasChart />;
	}

	if (!isInvestmentDomain(baseData.domain)) {
		return null;
	}

	const config = FUND_DOMAIN_CONFIG[baseData.domain];

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const { paidAllValue, shouldShowPaidAll } = getPaidAllLogic({ baseData });

	const paidAll = getPortfolioFrequencyValue(
		paidAllValue,
		config.usesCurrencySignForPaidAll
			? currencySign
			: t(PageResources.SharedLabels.Currency[baseData.currency]),
		ClientPortfolioPaymentFrequencyEnum.Monthly,
		t,
		0,
	);

	return (
		<>
			{daysData.length > 0 && (
				<DetailValueChart
					totalValue={totalValue}
					totalInvestedValue={totalInvestedValue}
					totalEvaluationPercent={
						reporting?.summary?.totalEvaluationPercent ?? 0
					}
					daysData={daysData}
					currencySign={currencySign}
					toDate={toDate}
					showChartInfo={showChartInfo}
					hideEvaluation={config.hidesEvaluation}
					onEditValue={onUpdateValue}
					bottomComponent={
						showChartInfo ? (
							<>
								<DetailRow
									label={t(Resources.Portfolio.Chart.InvestedTogether)}
									value={moneyFormatter(
										totalInvestedValue,
										currencySign,
										"",
										2,
									)}
								/>
								<DetailRow
									label={t(Resources.Common.ToDate)}
									value={getPortfolioValueDate(daysData, baseData)}
								/>
							</>
						) : undefined
					}
				/>
			)}

			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
				{shouldShowPaidAll && (
					<DetailRow
						label={t(PageResources.SharedLabels.paidAll)}
						value={paidAll}
					/>
				)}
			</DetailCard>
			<DetailDates data={baseData} />

			<FundListSection
				portfolioId={baseData.id}
				title={t(config.toolsTitle)}
				tools={normalizeTools(baseData.tools)}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				currencySign={currencySign}
			/>

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
